import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, Edit, Delete, AttachMoney, Search } from '@mui/icons-material';
import { ModuleLayout } from '../../components/Layout/ModuleLayout';
import PendingApprovalsSection from '../../components/common/PendingApprovalsSection';
import apiClient from '../../api/client';
import { moduleService, moduleChargeService, ModuleCharge } from '../../services';
import { usePermission } from '../../hooks';
import { colors } from '../../theme/theme';

export const ModuleChargesPage = () => {
  const { id } = useParams<{ id: string }>();
  const { can } = usePermission();
  const [module, setModule] = useState<any>(null);
  const [charges, setCharges] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [zones, setZones] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState<any>(null);
  const [formData, setFormData] = useState({
    service_id: '',
    zone_id: '',
    charge_type: 'fixed',
    amount: '',
    percentage_value: '',
    property_value_field: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [duplicateError, setDuplicateError] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [moduleRes, chargesRes, servicesRes, zonesRes] = await Promise.all([
        moduleService.getModule(Number(id)),
        apiClient.get(`/admin/county/modules/${id}/charges?per_page=1000`),
        apiClient.get(`/admin/county/modules/${id}/services`),
        apiClient.get(`/admin/county/zones?module_id=${id}`),
      ]);

      if (moduleRes.status === 'success') {
        setModule(moduleRes.data);
      }
      if (chargesRes.status === 'success' && chargesRes.data) {
        const chargesData = (chargesRes.data as any)?.data || chargesRes.data || [];
        setCharges(Array.isArray(chargesData) ? chargesData : []);
      }
      if (servicesRes.status === 'success') {
        const servicesData = servicesRes.data?.data || servicesRes.data || [];
        setServices(Array.isArray(servicesData) ? servicesData : []);
      }
      if (zonesRes.status === 'success') {
        const zonesData = zonesRes.data?.data || zonesRes.data || [];
        setZones(Array.isArray(zonesData) ? zonesData : []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Check if a charge already exists for a given service-zone combination
  const checkDuplicateCharge = (serviceId: string, zoneId: string): string => {
    if (!serviceId || !zoneId) return '';

    const existing = charges.find(
      (c) =>
        Number(c.service_id) === Number(serviceId) &&
        Number(c.zone_id) === Number(zoneId) &&
        (!selectedCharge || c.id !== selectedCharge.id)
    );

    if (existing) {
      const serviceName = services.find((s) => Number(s.id) === Number(serviceId))?.name || 'this service';
      const zoneName = zones.find((z) => Number(z.id) === Number(zoneId))?.name || 'this zone';
      return `A charge for "${serviceName}" in "${zoneName}" already exists. Please edit the existing charge instead.`;
    }
    return '';
  };

  const handleOpenDialog = (charge?: any) => {
    if (charge) {
      setSelectedCharge(charge);
      setFormData({
        service_id: charge.service_id?.toString() || '',
        zone_id: charge.zone_id?.toString() || '',
        charge_type: charge.charge_type || 'fixed',
        amount: charge.amount?.toString() || '',
        percentage_value: charge.percentage_value?.toString() || '',
        property_value_field: charge.property_value_field || '',
      });
    } else {
      setSelectedCharge(null);
      setFormData({
        service_id: '',
        zone_id: '',
        charge_type: 'fixed',
        amount: '',
        percentage_value: '',
        property_value_field: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCharge(null);
    setDuplicateError('');
    setFormData({
      service_id: '',
      zone_id: '',
      charge_type: 'fixed',
      amount: '',
      percentage_value: '',
      property_value_field: '',
    });
  };

  const handleSubmit = async () => {
    try {
      const serviceId = formData.service_id ? parseInt(formData.service_id) : undefined;
      const zoneId = formData.zone_id ? parseInt(formData.zone_id) : undefined;

      // Check for existing charge with same service-zone combination
      const dupError = checkDuplicateCharge(formData.service_id, formData.zone_id);
      if (dupError) {
        setDuplicateError(dupError);
        return;
      }

      const data: any = {
        service_id: serviceId,
        zone_id: zoneId,
        charge_type: formData.charge_type,
      };

      if (formData.charge_type === 'fixed') {
        data.amount = formData.amount ? parseFloat(formData.amount) : 0;
      } else {
        data.percentage_value = formData.percentage_value ? parseFloat(formData.percentage_value) : 0;
        data.property_value_field = formData.property_value_field;
      }

      let response;
      if (selectedCharge) {
        response = await moduleChargeService.update(Number(id!), selectedCharge.id, data);
        if (response.status === 'success') {
          setSnackbar({
            open: true,
            message: response.message || 'Update request submitted for approval',
            severity: 'success',
          });
          setTabValue(1);
        }
      } else {
        response = await moduleChargeService.create(Number(id!), data);
        if (response.status === 'success') {
          setSnackbar({
            open: true,
            message: 'Charge created successfully',
            severity: 'success',
          });
        }
      }
      fetchData();
      handleCloseDialog();
    } catch (err: any) {
      console.error('Failed to save charge:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.errors || 'Failed to save charge';
      setSnackbar({
        open: true,
        message: typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage),
        severity: 'error',
      });
    }
  };

  const handleDelete = async (chargeId: number) => {
    if (!window.confirm('Are you sure you want to submit a delete request for this charge?')) return;
    try {
      const response = await moduleChargeService.delete(Number(id!), chargeId);
      if (response.status === 'success') {
        setSnackbar({
          open: true,
          message: response.message || 'Delete request submitted for approval',
          severity: 'success',
        });
        setTabValue(1);
        fetchData();
      }
    } catch (err: any) {
      console.error('Failed to delete charge:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to delete charge',
        severity: 'error',
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Filter charges based on search query
  const filteredCharges = charges.filter((charge) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      charge.service?.name?.toLowerCase().includes(query) ||
      charge.zone?.name?.toLowerCase().includes(query) ||
      charge.charge_type?.toLowerCase().includes(query) ||
      charge.amount?.toString().includes(query) ||
      charge.percentage_value?.toString().includes(query)
    );
  });

  const columns: GridColDef[] = [
    {
      field: 'service',
      headerName: 'Service',
      width: 200,
      display: 'flex',
      valueGetter: (value, row) => row?.service?.name || 'N/A',
    },
    {
      field: 'zone',
      headerName: 'Zone',
      width: 150,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value?.name || 'N/A'}
          size="small"
          sx={{ fontWeight: 600, bgcolor: '#eff6ff', color: '#1e40af' }}
        />
      ),
    },
    {
      field: 'charge_type',
      headerName: 'Charge Type',
      width: 150,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value === 'percentage' ? 'Percentage' : 'Fixed Amount'}
          size="small"
          sx={{
            fontWeight: 600,
            bgcolor: params.value === 'percentage' ? '#fef3c7' : '#dbeafe',
            color: params.value === 'percentage' ? '#92400e' : '#1e40af'
          }}
        />
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount/Rate',
      width: 200,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row;
        return row.charge_type === 'percentage' ? (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#ea580c' }}>
              {row.percentage_value}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              of {row.property_value_field?.replace(/_/g, ' ')}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#16a34a' }}>
            {formatCurrency(row.amount)}
          </Typography>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value || 'Active'}
          color="success"
          size="small"
          sx={{ fontWeight: 600, textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {can('charges.edit') && (
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(params.row)}
              sx={{ color: colors.primary.main }}
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
          {can('charges.delete') && (
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              sx={{ color: '#dc2626' }}
            >
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <ModuleLayout module={module} moduleId={id!}>
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout module={module} moduleId={id!}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 1 }}>
              Charges
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Manage pricing and fees for module services
            </Typography>
          </Box>
          {can('charges.create') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Add Charge
            </Button>
          )}
        </Box>

        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Charges" />
          <Tab label="Approvals" />
        </Tabs>

        {tabValue === 0 && (
          <>
            <TextField
              fullWidth
              placeholder="Search charges by service, zone, type, or amount..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={filteredCharges}
                columns={columns}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                disableRowSelectionOnClick
                sx={{
                  '& .MuiDataGrid-cell:focus': {
                    outline: 'none',
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
                slots={{
                  noRowsOverlay: () => (
                    <Box sx={{ textAlign: 'center', py: 8, mt: 4 }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          bgcolor: colors.primary[50],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                        }}
                      >
                        <AttachMoney sx={{ fontSize: 40, color: colors.primary.main }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        No Charges Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Create your first pricing charge
                      </Typography>
                      {can('charges.create') && (
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => handleOpenDialog()}
                          sx={{
                            background: colors.gradients.primary,
                          }}
                        >
                          Add Charge
                        </Button>
                      )}
                    </Box>
                  ),
                }}
              />
            </Box>
          </>
        )}

        {tabValue === 1 && (
          <PendingApprovalsSection
            entityType="charge"
            title="Pending Charge Approvals"
            onApprovalComplete={fetchData}
          />
        )}

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{selectedCharge ? 'Edit Charge' : 'Add Charge'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Service</InputLabel>
                <Select
                  value={formData.service_id}
                  label="Service"
                  onChange={(e) => {
                    const newServiceId = e.target.value as string;
                    setFormData({ ...formData, service_id: newServiceId, zone_id: '' });
                    setDuplicateError('');
                  }}
                >
                  <MenuItem value="">
                    <em>Select Service</em>
                  </MenuItem>
                  {services.map((service) => {
                    const chargedZoneCount = charges.filter(
                      (c) => Number(c.service_id) === service.id && (!selectedCharge || c.id !== selectedCharge.id)
                    ).length;
                    const allZonesCharged = chargedZoneCount > 0 && chargedZoneCount >= zones.length;
                    return (
                      <MenuItem key={service.id} value={service.id.toString()} disabled={allZonesCharged}>
                        {service.name}{allZonesCharged ? ' (all zones charged)' : chargedZoneCount > 0 ? ` (${chargedZoneCount} zone${chargedZoneCount > 1 ? 's' : ''} charged)` : ''}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl fullWidth required error={!!duplicateError}>
                <InputLabel>Zone</InputLabel>
                <Select
                  value={formData.zone_id}
                  label="Zone"
                  onChange={(e) => {
                    const newZoneId = e.target.value as string;
                    setFormData({ ...formData, zone_id: newZoneId });
                    setDuplicateError(checkDuplicateCharge(formData.service_id, newZoneId));
                  }}
                  disabled={!formData.service_id}
                >
                  <MenuItem value="">
                    <em>Select Zone</em>
                  </MenuItem>
                  {zones.map((zone) => {
                    const hasCharge = charges.some(
                      (c) => Number(c.service_id) === Number(formData.service_id) && Number(c.zone_id) === zone.id && (!selectedCharge || c.id !== selectedCharge.id)
                    );
                    return (
                      <MenuItem key={zone.id} value={zone.id.toString()} disabled={hasCharge}>
                        {zone.name}{hasCharge ? ' (charge exists)' : ''}
                      </MenuItem>
                    );
                  })}
                </Select>
                {duplicateError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {duplicateError}
                  </Alert>
                )}
                {!formData.service_id && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Select a service first to see available zones
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel>Charge Type</InputLabel>
                <Select
                  value={formData.charge_type}
                  label="Charge Type"
                  onChange={(e) => setFormData({ ...formData, charge_type: e.target.value as string })}
                >
                  <MenuItem value="fixed">Fixed Amount</MenuItem>
                  <MenuItem value="percentage">Percentage of Property Value</MenuItem>
                </Select>
              </FormControl>

              {formData.charge_type === 'fixed' ? (
                <TextField
                  fullWidth
                  required
                  label="Amount (KES)"
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  InputProps={{
                    inputProps: { min: 0, step: 0.01 }
                  }}
                />
              ) : (
                <>
                  <TextField
                    fullWidth
                    required
                    label="Percentage (%)"
                    type="number"
                    value={formData.percentage_value}
                    onChange={(e) => setFormData({ ...formData, percentage_value: e.target.value })}
                    InputProps={{
                      inputProps: { min: 0, max: 100, step: 0.01 }
                    }}
                    helperText="Enter the percentage rate (e.g., 2.5 for 2.5%)"
                  />
                  <FormControl fullWidth required>
                    <InputLabel>Property Value Field</InputLabel>
                    <Select
                      value={formData.property_value_field}
                      label="Property Value Field"
                      onChange={(e) => setFormData({ ...formData, property_value_field: e.target.value as string })}
                    >
                      <MenuItem value="">
                        <em>Select Field</em>
                      </MenuItem>
                      <MenuItem value="property_value">Property Value</MenuItem>
                      <MenuItem value="land_value">Land Value</MenuItem>
                      <MenuItem value="improvement_value">Improvement Value</MenuItem>
                      <MenuItem value="market_value">Market Value</MenuItem>
                      <MenuItem value="assessed_value">Assessed Value</MenuItem>
                    </Select>
                  </FormControl>
                  <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                    The charge will be calculated as {formData.percentage_value || '0'}% of the selected property value field when the service is billed.
                  </Alert>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!!duplicateError}
              sx={{
                background: colors.gradients.primary,
              }}
            >
              {selectedCharge ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ModuleLayout>
  );
};
